import { useState, useEffect, useCallback } from "react";
import axiosInstance from "config/axiosConfig";
import { useDeviceInfo } from "./useDeviceInfo";

interface UserLocation {
  latitude: number;
  longitude: number;
}

export const useGeolocation = () => {
  const [userLocation, setUserLocation] = useState<UserLocation | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [isLocationAllowed, setIsLocationAllowed] = useState<boolean>(false);
  const [isLoadingLocation, setIsLoadingLocation] = useState<boolean>(false);
  const { browser, device } = useDeviceInfo();

  const handleLocationError = (error: GeolocationPositionError) => {
    switch (error.code) {
      case error.PERMISSION_DENIED:
        setLocationError(
          "Location permission is required to log in. Please enable location access."
        );
        break;
      case error.POSITION_UNAVAILABLE:
        setLocationError("Location information is unavailable.");
        break;
      case error.TIMEOUT:
        setLocationError("The request to get user location timed out.");
        break;
      default:
        setLocationError("An error occurred while fetching location.");
    }
  };

  const fetchLocation = () => {
    if (!navigator.geolocation) {
      setLocationError("Geolocation is not supported by this browser.");
      return;
    }

    setIsLoadingLocation(true);

    navigator.permissions
      .query({ name: "geolocation" })
      .then((permissionStatus) => {
        if (permissionStatus.state === "denied") {
          setLocationError(
            "Location access is denied. Please enable location permissions in your browser settings."
          );
          setIsLoadingLocation(false);
        } else {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const { latitude, longitude } = position.coords;
              setUserLocation({ latitude, longitude });
              setIsLocationAllowed(true);
              setIsLoadingLocation(false);
            },
            (error) => {
              handleLocationError(error);
              setIsLoadingLocation(false);
            },
            { timeout: 10000 }
          );
        }
      })
      .catch(() => {
        setLocationError("Error checking location permissions.");
        setIsLoadingLocation(false);
      });
  };

  const postLocation = useCallback(
    async (userId: string) => {
      if (!userId || !userLocation || !isLocationAllowed) {
        console.warn(
          "Cannot post location: missing userId, location, or permission."
        );
        return;
      }

      try {
        const metadata = { browser, device };

        const payload = {
          service: "auth-service",
          eventType: "USER_LOGIN",
          userId,
          location: userLocation,
          metadata,
        };

        await axiosInstance.post("/event", payload);
      } catch (error) {
        console.error("Error posting location:", error);
      }
    },
    [userLocation, isLocationAllowed, browser, device]
  );

  useEffect(() => {
    fetchLocation();
  }, []);

  return {
    userLocation,
    locationError,
    isLocationAllowed,
    isLoadingLocation,
    postLocation,
  };
};
